import React from "react";
import ReactDOM from "react-dom";
import {
  PaymentForm,
  CreditCard as SquareCreditCard,
} from "react-square-web-payments-sdk";
import { toast } from "react-toastify";
import axios from "axios";

/**
 * SquarePaymentVoucher.jsx
 * -------------------------
 * Handles SquareUp payments for Gift Vouchers only.
 * Opens in a modal (React Portal), processes card payment,
 * and notifies the parent component with a Stripe-like paymentIntent object.
 *
 * Props:
 *  - open (boolean): whether modal is visible
 *  - onClose (function): close handler
 *  - amount (number): amount to charge
 *  - settings (object): contains squareup_app_id, squareup_location, squareup_live
 *  - onSuccess (function): callback(paymentIntent) when payment succeeds
 */
const SquarePaymentVoucher = ({ open, onClose, amount, settings = {}, onSuccess }) => {
  if (!open) return null;

  // Handle Square payment token response
  const handlePayment = async (tokenResult) => {
    try {
      const { token } = tokenResult;

      // Call backend to process payment and save gift voucher
      const { data } = await axios.post(`${window.BASE_API_URL}squareup_success`, {
        token,
        amount,
        live: settings?.squareup_live || 0,
        payment_method: "squareup",
        payment_status: "COMPLETED",
      });

      // console.log("🎁 Square Gift Voucher API Response:", data);

      if (data?.success || data?.payment?.status === "COMPLETED") {
        toast.success("✅ Square Payment Successful!");

        // Build Stripe-like paymentIntent structure
        const paymentIntent = {
          id: data?.payment?.id || `GIFT-SQ-${Date.now()}`,
          object: "payment_intent",
          amount: Math.round(amount * 100), // Stripe uses cents
          currency: localStorage.getItem("currency_name") || "USD",
          status: "succeeded",
          payment_method_types: ["squareup"],
          provider: "square",
          created: Date.now(),
          raw: data,
        };

        // Pass this paymentIntent back to parent (GiftVoucher.js)
        onSuccess(paymentIntent);
        onClose();
      } else {
        toast.error("❌ Square payment failed. Please try again.");
      }
    } catch (err) {
      console.error("❌ Square Payment Error:", err);
      toast.error("Square payment failed. Please try again.");
    }
  };

  // Determine environment
  const environment =
    settings?.squareup_live === 1 ? "PRODUCTION" : "SANDBOX";

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[999999]">
      <div className="bg-white rounded-2xl shadow-2xl p-6 w-[420px] relative animate-fadeIn">
        <h3 className="text-2xl font-semibold mb-4 text-gray-800 text-center">
          💳 Pay with Square
        </h3>

        <p className="text-gray-500 text-center mb-6">
          Total:{" "}
          <span className="font-bold text-gray-800">
            {localStorage.getItem("currency") || "$"} {amount.toFixed(2)}
          </span>
        </p>

        <div className="border border-gray-200 rounded-xl p-4 shadow-inner">
          <PaymentForm
            applicationId={settings?.squareup_app_id}
            locationId={settings?.squareup_location}
            environment={environment}
            cardTokenizeResponseReceived={handlePayment}
          >
            <SquareCreditCard
              buttonProps={{
                children: `Pay $${Number(amount).toFixed(2)}`,
                css: {
                  backgroundColor: "#16a34a",
                  color: "#fff",
                  borderRadius: "8px",
                  fontSize: "16px",
                  fontWeight: "bold",
                  padding: "10px 16px",
                  width: "100%",
                  marginTop: "8px",
                },
              }}
            />
          </PaymentForm>
        </div>

        <button
          onClick={onClose}
          className="absolute top-3 right-4 text-gray-500 hover:text-gray-800 transition"
        >
          ✕
        </button>

        <p className="text-xs text-gray-400 text-center mt-4">
          Secure payment powered by Square
        </p>
      </div>
    </div>,
    document.body
  );
};

export default SquarePaymentVoucher;
